import { toast } from "sonner";

// Types for constants

export const isMobile = (val: string): boolean => {
  // 10-digit mobile number
  return /^\d*$/.test(val) && val.length === 10;
};

export const isNumber = (val: string, len?: number): boolean => {
  // Numeric value of certain length
  if (len !== undefined) {
    return /^\d*$/.test(val) && val.length === len;
  }
  return /^\d*$/.test(val);
};

export const isValidPassword = (val: string): boolean => {
  // Check if the text contains at least one lowercase letter, one uppercase letter, one number, and is at least 6 characters long
  const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{6,}$/;
  return regex.test(val);
};

export const isAlphanumeric = (val: string): boolean => {
  // Check if the string contains only letters and numbers
  const regex = /^[a-zA-Z0-9]+$/;
  return regex.test(val);
};

export const handleCopy = (text: string): void => {
  // Create a textarea element to copy the text
  const textarea = document.createElement("textarea");
  textarea.value = text;
  document.body.appendChild(textarea);

  // Select the text in the textarea
  textarea.select();

  // Copy the selected text to the clipboard
  document.execCommand("copy");

  // Remove the textarea element
  document.body.removeChild(textarea);

  // Alert the user that the text has been copied
  toast("Text copied to clipboard!");
};

export function removeNonNumeric(str: string): string {
  return str.replace(/\D/g, "");
}

export function formatToRupee(amount: number): string {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
  }).format(amount);
}

export function getRandomNumbers(len: number, till: number = 25): number[] {
  const length = len % till;
  const availableNumbers = Array.from(Array(till).keys());
  const result: number[] = [];

  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * availableNumbers.length);
    result.push(availableNumbers[randomIndex]);
    availableNumbers.splice(randomIndex, 1); // Remove the selected number to avoid repetition
  }

  return result;
}

export function getRandomNumber(max: number): number {
  return Math.floor(Math.random() * max);
}

export function totalSeconds(): number {
  const date = new Date(); // current date
  return Math.floor(date.getTime() / 1000);
}

export const compressText = (id: string): string => {
  if (!id) return ""; // Handle the case if the id is null or undefined
  return `${id.slice(0, 4)}...${id.slice(-3)}`;
};
// Encode function
export function encodeBase64(input: string): string {
  return btoa(input); // Converts the string into Base64
}

// Decode function
export function decodeBase64(encodedInput: string): string | 0 {
  try {
    const cleanedInput = encodedInput?.trim().replace(/\s/g, "");

    const padLength = 4 - (cleanedInput.length % 4);
    const paddedInput =
      padLength < 4 ? cleanedInput + "=".repeat(padLength) : cleanedInput;

    return atob(paddedInput);
  } catch (error) {
    return 0;
  }
}

export const sanitizeInput = (value: string) => {
  const hasScript =
    /<script.*?>.*?<\/script>/gi.test(value) ||
    /javascript:/gi.test(value) ||
    /on\w+\s*=/gi.test(value);

  return {
    sanitizedValue: value
      .replace(/<script.*?>.*?<\/script>/gi, "")
      .replace(/javascript:/gi, "")
      .replace(/on\w+\s*=/gi, ""),
    hasScript,
  };
};

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;       // 5 MB
const MAX_VIDEO_SIZE = 16 * 1024 * 1024;      // 16 MB
const MAX_DOCUMENT_SIZE = 100 * 1024 * 1024;  // 100 MB

const ALLOWED_MIME_TYPES: Record<string, string[]> = {
  Image: ["image/jpeg", "image/jpg", "image/png"], // SVG & GIF excluded
  Video: ["video/mp4", "video/3gpp"],
  Document: ["application/pdf"],
};

const MAX_SIZE_BY_TYPE: Record<string, number> = {
  Image: MAX_IMAGE_SIZE,
  Video: MAX_VIDEO_SIZE,
  Document: MAX_DOCUMENT_SIZE,
};

export const validateMediaFile = (
  file: File,
  headerType: string
): { valid: boolean; error?: string } => {
  const allowedTypes = ALLOWED_MIME_TYPES[headerType];
  const maxSize = MAX_SIZE_BY_TYPE[headerType];

  if (!allowedTypes || !maxSize) {
    return { valid: false, error: "Invalid header type" };
  }

  if (!allowedTypes.includes(file.type)) {
    const friendlyFormats =
      headerType === "Image"
        ? "JPEG, JPG, PNG"
        : headerType === "Video"
        ? "MP4, 3GPP"
        : "PDF";
    return {
      valid: false,
      error: `Unsupported file format. Allowed: ${friendlyFormats}`,
    };
  }

  if (file.size > maxSize) {
    const maxSizeMB = maxSize / (1024 * 1024);
    return {
      valid: false,
      error: `${headerType} size should be less than ${maxSizeMB}MB`,
    };
  }

  return { valid: true };
};

export const formatDate = (epoch: number) => {
  if (!epoch) return "-";

  return new Date(epoch).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const fmtDelay = (m:number) => {
  if (!m) return 'Immediately';
  if (m % 1440 === 0) return `${m / 1440} day${m === 1440 ? '' : 's'} later`;
  if (m % 60 === 0) return `${m / 60} hour${m === 60 ? '' : 's'} later`;
  return `${m} min later`;
};

export async function copyText(text:string) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}